import type { FC } from 'react';
import React, { Fragment } from 'react';

import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint, LoadableHydrateOnHover } from '@confluence/loadable';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import {
	checkSSRPartialSuccess,
	SSR_PARTIAL_COMPONENT,
} from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';

import type { PageTreeProps } from '../src/PageTree.tsx';
import { expVal } from '@confluence/feature-experiments';

const DELAY_HYRATION_ON_HOVER_FF = 'confluence_frontend_delay_hydration_on_hover';

export const ANIMATE_TIME_SEC = 2;

export const PageTreeLoader: LoadableType<PageTreeProps> = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-PageTree" */ '../src/PageTree.tsx')).PageTree,
});

export const PageTreeLoaderOnHover: LoadableType<PageTreeProps> = LoadableHydrateOnHover({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-PageTreeOnHover" */ '../src/PageTree.tsx'))
			.PageTree,
	hydrateImmediately: () => {
		// If page tree was not successfully rendered on server-side, hydrate immediately otherwise user will see a blank page tree
		return !checkSSRPartialSuccess(SSR_PARTIAL_COMPONENT.PageTree);
	},
});

export const PageTree: FC<PageTreeProps> = (props) => {
	const delayHydrationOnHoverTimeout =
		StatsigConfigurations.getNumber(DELAY_HYRATION_ON_HOVER_FF, 0) || 0;
	return (
		<Fragment>
			{!expVal('confluence_frontend_remove_hydrateonhover_pagetree', 'isEnabled', false) &&
			delayHydrationOnHoverTimeout > 0 ? (
				<PageTreeLoaderOnHover {...props} />
			) : (
				<PageTreeLoader {...props} />
			)}
		</Fragment>
	);
};
